/**
 * Addresses that many companies point at.
 *
 * This is the accountancy and company-formation problem made visible: one firm
 * registers hundreds of companies and puts its own address on all of them. On
 * the production mirror, 210 addresses cover 13 547 companies, and 18.6% of the
 * register would be a duplicate delivery without the shared-address rule.
 *
 * Excluding them is the right default, but hiding them is not: one of these is
 * worth a single letter written by a person, which is worth more than forty
 * automated ones. So the list is here, next to the audience, rather than only
 * as a number the screening quietly subtracts.
 */
import { useEffect, useState } from "react";
import { Loader2, Users2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { listSharedAddresses } from "../../../../lib/marketing";
import { Bar, num } from "../shared";

export function SharedAddresses({ threshold }: { threshold: number }) {
  const t = useT();
  const [rows, setRows] = useState<{ email: string; companies: number }[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(false);
    listSharedAddresses(threshold, 25)
      .then((res) => {
        if (alive) setRows(res.rows);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [threshold]);

  // Scaled against the worst offender rather than against the threshold: the
  // point of the list is the shape of the tail, and one address behind a
  // thousand companies is a different animal from one behind six.
  const worst = rows?.[0]?.companies ?? 1;

  return (
    <section className="rounded-2xl border border-border bg-card/70 p-5 shadow-soft backdrop-blur">
      <header className="mb-3 flex items-start gap-2.5">
        <Users2 className="mt-0.5 size-4 shrink-0 text-primary" />
        <div>
          <h3 className="text-sm font-semibold tracking-tight">{t("marketing.shared.title")}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t("marketing.shared.intro", { n: threshold })}
          </p>
        </div>
      </header>

      {error && <p className="text-xs text-destructive">{t("marketing.error.shared")}</p>}
      {rows === null && !error && <Loader2 className="size-4 animate-spin text-muted-foreground" />}

      {rows && rows.length === 0 && <p className="text-xs text-muted-foreground">{t("marketing.shared.none")}</p>}

      {rows && rows.length > 0 && (
        <ul className="max-h-80 space-y-2 overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.email} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="min-w-0 truncate font-mono text-[11px]">{r.email}</span>
                <span className="shrink-0 font-mono tabular-nums text-muted-foreground">{num(r.companies)}</span>
              </div>
              <Bar value={r.companies} total={worst} tone="muted" />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
